import { Injectable } from "@angular/core";
import { AppError, AppErrorType, CoreHttpClient } from "@obg/ngx.core";
import { RequestPayload } from "@obg/ngx.global";
import { head, isEmpty, unescape } from "lodash-es";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { AffiliateService } from "../../affiliate/affiliate.service";

import { CustomerOfferConfigService } from "./customer-offer.config";
import { CustomerOffer, CustomerOfferFilter, OfferStateResponse } from "./customer-offer.model";

@Injectable({
	providedIn: "root",
})
export class CustomerOfferService {
	constructor(
		private httpClient: CoreHttpClient,
		private config: CustomerOfferConfigService,
		private affiliateService: AffiliateService
	) {}

	getOfferState(): Observable<CustomerOffer[]> {
		return this.httpClient
			.get<OfferStateResponse>({
				url: this.config.getCustomerOfferStateEndpoint(),
				searchParams: {
					customerOfferFilter: CustomerOfferFilter.percentDepositOffer,
				},
			})
			.pipe(map((offers) => this.extractOffers(offers)));
	}

	getCampaignOfferState(): Observable<CustomerOffer[]> {
		const affiliateCode = this.affiliateService.getCookieName();
		const url = affiliateCode
			? `${this.config.getCampaignOfferStateEndpoint()}&affiliateCode=:affiliateCode`
			: this.config.getCampaignOfferStateEndpoint();

		const payload: RequestPayload = {
			url,
			searchParams: {
				offerType: CustomerOfferFilter.quickRegistrationWelcomeOffer,
			},
		};

		if (affiliateCode) {
			payload.searchParams = { ...payload.searchParams, affiliateCode };
		}

		return this.httpClient.get<OfferStateResponse>(payload).pipe(
			map((offers) => this.extractOffers(offers)),
			catchError((error: AppError) => {
				if (error.type === AppErrorType.NotFound) {
					return of([]);
				}
				return throwError(() => error);
			})
		);
	}

	private extractOffers(offers: OfferStateResponse) {
		if (!offers || !offers.customerOffers) {
			return [];
		}

		return offers.customerOffers
			.filter((x) => x.offer.content && !isEmpty(x.offer.content))
			.map((customerOffer) => this.processRawReward(customerOffer));
	}

	private processRawReward(customerOffer: CustomerOffer): CustomerOffer {
		const { imageContentItems, textContentItems } = customerOffer.offer.content!;
		return {
			...customerOffer,
			offer: {
				...customerOffer.offer,
				image: imageContentItems && head(imageContentItems.image),
				title: textContentItems ? unescape(textContentItems.title) : undefined,
				description: textContentItems ? unescape(textContentItems.description) : undefined,
				termsAndConditions: textContentItems ? unescape(textContentItems.termsAndConditions) : undefined,
			},
		};
	}
}
